import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Modal } from "../Modal";
import MarkdownInput from "../MarkdownInput";
import MarkdownTextArea from "../MarkdownTextArea";
import Button from "../Button";
import { DropdownMenu } from "../DropdownMenu";
import { IDropdownMenuOption } from "../DropdownMenu/DropdownMenu";
import {
    IEmptySeed,
    ISeedCategory,
    ISeedProductSubmit,
    ISeedAttributeValue,
    ISeedAttribute,
    ISeedIcon,
    ISeedProduct,
} from "../../infrastructure/interfaces";
import { saveSeedProduct, getSeedIcons } from "../../actions/actions";

interface ISeedFormState {
    seed_icons: (ISeedIcon | undefined)[];
    id: number | undefined;
    name: string;
    published: boolean;
    isNew: boolean;
    brand: boolean;
    category: ISeedCategory | undefined;
    bulletPointMarkdown: string;
    attributes: ISeedAttributeValue[];
    formValid: boolean;
}

//from redux store
interface StateProps {
    seedCategoryList: ISeedCategory[];
    seedIconList: ISeedIcon[];
}
const mapStateToProps = (state: any): StateProps => {
    return {
        seedCategoryList: state.seedCategoryList,
        seedIconList: state.seedIconList,
    };
};

//from redux store
interface DispatchProps {
    saveSeedProduct(seed: ISeedProductSubmit): void;
    getSeedIcons(): void;
}

const mapDispatchToProps = (dispatch: any): DispatchProps => {
    return {
        saveSeedProduct: (seed: ISeedProductSubmit) => {
            dispatch(saveSeedProduct(seed, false));
        },
        getSeedIcons: () => {
            dispatch(getSeedIcons());
        },
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type ISeedFormProps = PropsFromRedux & {
    seed: ISeedProduct | IEmptySeed | undefined;
    onClose: () => void;
    show: boolean;
};

class SeedForm extends React.Component<ISeedFormProps, ISeedFormState> {
    //holds the edited values in react state, gets saved seed data from props
    constructor(props: ISeedFormProps) {
        super(props);
        const emptySeed: IEmptySeed = {
            id: undefined,
            name: "",
            published: false,
            isNew: false,
            brand: false,
            category: undefined,
            bulletPointMarkdown: "",
            attributes: [],
            icons: [],
            type: "",
        };

        const editingSeed = this.props.seed || emptySeed;

        //server expects an array of seed icons, no way to store like just icon 2 without icon 1
        //so map to 3 element array potentially undefined at each index
        //const seed = this.props.seed;
        let mappedIcons: (ISeedIcon | undefined)[] = [];
        for (let i = 0; i < 3; i++) {
            if (editingSeed.icons.length > i) {
                mappedIcons.push(editingSeed.icons[i]);
            } else {
                mappedIcons.push(undefined);
            }
        }

        const formValid = this.isFormValid(editingSeed);

        this.state = { ...editingSeed, seed_icons: mappedIcons, formValid };
        this.onSaveSeedProduct = this.onSaveSeedProduct.bind(this);
    }

    componentDidMount() {
        //this.props.getSeedCategories();
        this.props.getSeedIcons();
    }

    componentDidUpdate(_: ISeedFormProps, prevState: ISeedFormState) {
        // Compare previous state and current state to determine if validity check
        // need to be performed
        const validatableFieldsChanged = this.checkValidatableFieldsForChange(
            prevState,
            this.state
        );

        // If validatable fields have changed perform a check on their validity
        if (validatableFieldsChanged) {
            const formValid = this.isFormValid(this.state);
            this.setState({ formValid });
        }
    }

    isFormValid(state: ISeedFormState | IEmptySeed): boolean {
        const { name, bulletPointMarkdown, category } = state;

        // name must be defined. as this value comes from an input it won't be
        // undefined, but it might have a length of 0.
        if (name.length <= 0) {
            return false;
        }

        // bulletPointMarkdown must be defined. as this value comes from an input it won't be
        // undefined, but it might have a length of 0.
        if (bulletPointMarkdown.length > 550) {
            return false;
        }

        // no selected category
        if (!category) {
          return false;
        }

        return true;
    }

    checkValidatableFieldsForChange(
        prevState: ISeedFormState,
        curState: ISeedFormState
    ): boolean {
        const {
            name: prevName,
            bulletPointMarkdown: prevBulletPointMarkdown,
            category: prevCategory
        } = prevState;
        const {
            name: curName,
            bulletPointMarkdown: curBulletPointMarkdown,
            category: curCategory
        } = curState;

        // changed the seed name
        if (prevName !== curName) {
            return true;
        }

        // changed the seed bullet points
        if (prevBulletPointMarkdown !== curBulletPointMarkdown) {
            return true;
        }

        // changed the category
        if (prevCategory !== curCategory) {
          return true;
        }

        return false;
    }

    onSaveSeedProduct(history: any) {
        function notEmpty<TValue>(
            value: TValue | null | undefined
        ): value is TValue {
            return value !== null && value !== undefined;
        }
        if (this.state.category) {
            const validated: ISeedProductSubmit = {
                id: this.state.id,
                name: this.state.name,
                type: "Seed",
                published: this.state.published,
                bulletPointMarkdown: this.state.bulletPointMarkdown,
                isNew: this.state.isNew,
                brand: this.state.brand,

                categoryId: this.state.category.id,
                //attributes: [{ attributeId: 1, value: "9" }],
                attributes: this.state.attributes.map((a) => {
                    return { attributeId: a.attribute.id, value: a.value };
                }),
                //server expects an array of seed icons, no way to store like just icon 2 without icon 1
                //so map from 3 element array potentially undefined at each index, to a
                //list of the selected icons
                icons: this.state.seed_icons.filter(notEmpty),
            };
            this.props.saveSeedProduct(validated);

            this.props.onClose();
        } //else show a validation error
    }
    createSeedCategoryList(): IDropdownMenuOption[] {
        var seedCategoryList: IDropdownMenuOption[] = [
            {
                title: "Clear Selection",
                onClick: () => {
                    this.setState({
                        category: undefined,
                    });
                },
            },
        ];
        if (this.props.seedCategoryList) {
            seedCategoryList = seedCategoryList.concat(
                this.props.seedCategoryList.map((p) => {
                    return {
                        title: p.name,
                        onClick: () => {
                            this.handleSeedCategoryChange(p);
                        },
                    };
                })
            );
        }

        return seedCategoryList;
    }
    createSeedIconList(iconIndex: number): IDropdownMenuOption[] {
        const category = this.state.category;
        if (category == undefined) {
            return [];
        }

        var seedIconList: IDropdownMenuOption[] = [
            {
                title: "Clear Selection",
                onClick: () => this.selectSeedIcon(iconIndex, undefined),
            },
        ];
        if (this.props.seedIconList) {
            seedIconList = seedIconList.concat(
                this.props.seedIconList
                    .filter((s) => (s.published && (s.category.id == category.id)))
                    .map((p) => {
                    return {
                        title: p.name,
                        onClick: () => this.selectSeedIcon(iconIndex, p),
                    };
                }).sort((a, b) => a.title.localeCompare(b.title))
            );
        }

        return seedIconList;
    }
    getSeedIconTitle(ix: number): string {
        const x = this.state.seed_icons[ix];
        if (x) return x.name;
        else return `Select Trait Logo ${ix + 1}`;
    }
    selectSeedIcon(
        seedIconIndex: number,
        selectedElement: ISeedIcon | undefined
    ) {
        if (selectedElement) {
            const updatedIcons = [...this.state.seed_icons];
            updatedIcons[seedIconIndex] = selectedElement;
            this.setState({ seed_icons: updatedIcons });
        }
        //deselecting an icon
        else {
            //selected no icon, so deselect it
            const updatedIcons = [...this.state.seed_icons];
            updatedIcons[seedIconIndex] = undefined;
            this.setState({ seed_icons: updatedIcons });
        }
    }
    createSeedAttributeFields() {
        let existingCategory: ISeedCategory;
        if (this.state.category) {
            existingCategory = this.state.category;
        } else {
            //doesn't exist, no attributes
            return [];
        }
        //needs the list of attributes, for that we need to look up this seed's category
        //in the list of all categories, because that has the associated attributes
        const category:
            | ISeedCategory
            | undefined = this.props.seedCategoryList.find(
            (c) => c.id === existingCategory.id
        );
        if (!category) return [];

        const fields = category.attributes.map((attribute: ISeedAttribute) => {
            //attrState is the current value of the attributeValue, the one that's save in state
            const attrState = this.state.attributes.find(
                (attrValue: ISeedAttributeValue) =>
                    attrValue.attribute.id === attribute.id
            );
            return (
                <div
                    className="productInfoInputContainer"
                    key={`seed-attribute-${attribute.id}`}
                >
                    <label>{attribute.createSeedDisplayName}</label>

                    <input
                        type="text"
                        value={attrState ? attrState.value : ""}
                        onChange={(event) =>
                            this.handleInputSeedAttributeChange(
                                attribute,
                                event
                            )
                        }
                    />
                </div>
            );
        });
        return fields;
    }

    handleSeedNameChange(name: string) {
        this.setState({ name });
    }

    handleSeedBulletPointMarkdownChange(bulletPointMarkdown: string) {
        this.setState({ bulletPointMarkdown });
    }

    handleSeedCategoryChange(category: ISeedCategory) {
        this.setState({ category });
    }

    handleInputSeedAttributeChange(
        attribute: ISeedAttribute,
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const text = event.currentTarget.value;
        const attributes = [...this.state.attributes];
        const index = attributes.findIndex(
            (a: ISeedAttributeValue) => a.attribute.id === attribute.id
        );
        if (index > -1) {
            attributes[index].value = text;
        } else {
            const newValue: ISeedAttributeValue = {
                attribute: attribute,
                id: undefined,
                value: text,
            };
            attributes.push(newValue);
        }

        this.setState({
            attributes: attributes,
        });
    }
    renderForm() {
        const seedAttributeFields = this.createSeedAttributeFields();
        return (
            <div className="productInfoContainer seedForm">
                <div className="product-form-container">
                    <div className="productInfoInputContainer">
                        <label>Product Name *</label>
                        <MarkdownInput
                            textValue={this.state.name}
                            onChange={(text) => this.handleSeedNameChange(text)}
                        />
                    </div>
                    <div className="productInfoInputContainer">
                        <label>Seed Category *</label>
                        <div>
                            <DropdownMenu
                                title={
                                    this.state.category
                                        ? this.state.category.name
                                        : "Select Seed Category"
                                }
                                options={this.createSeedCategoryList()}
                                extraWide={true}
                            />
                        </div>
                    </div>
                    <div className="productInfoInputContainer">
                        <label>New</label>
                        <div>
                            <input
                                type="checkbox"
                                checked={this.state.isNew}
                                onChange={(event) =>
                                    this.setState({
                                        isNew: event.target.checked,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div className="productInfoInputContainer">
                        <label>Brand</label>
                        <div>
                            <input
                                type="checkbox"
                                checked={this.state.brand}
                                onChange={(event) =>
                                    this.setState({
                                        brand: event.target.checked,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <hr />
                    <div className="productInfoInputContainer">
                        <label>Bullet Points (max of 550 characters)</label>
                        <MarkdownTextArea
                            textValue={this.state.bulletPointMarkdown}
                            onChange={(text) =>
                                this.handleSeedBulletPointMarkdownChange(text)
                            }
                        />
                    </div>

                    <div className="productInfoInputContainer">
                        <label>Trait Logo 1</label>
                        <div>
                            <DropdownMenu
                                title={this.getSeedIconTitle(0)}
                                options={this.createSeedIconList(0)}
                                extraWide={true}
                            />
                        </div>
                    </div>
                    <div className="productInfoInputContainer">
                        <label>Trait Logo 2</label>
                        <div>
                            <DropdownMenu
                                title={this.getSeedIconTitle(1)}
                                options={this.createSeedIconList(1)}
                                extraWide={true}
                            />
                        </div>
                    </div>
                    <div className="productInfoInputContainer">
                        <label>Trait Logo 3</label>
                        <div>
                            <DropdownMenu
                                title={this.getSeedIconTitle(2)}
                                options={this.createSeedIconList(2)}
                                extraWide={true}
                            />
                        </div>
                    </div>
                    <hr />
                    <h3>Seed Attributes</h3>
                    {seedAttributeFields.length ? (
                        seedAttributeFields
                    ) : (
                        <div>Select a category</div>
                    )}
                </div>
            </div>
        );
    }
    render() {
        return (
            <Modal show={this.props.show} onClose={this.props.onClose}>
                <Modal.Header>
                    <Modal.Title>
                        <h2>Seed</h2>
                        <h5>
                            <span>*Required Field</span>
                        </h5>
                    </Modal.Title>
                    <Button
                        bsClass="close"
                        onClick={this.props.onClose}
                    ></Button>
                </Modal.Header>
                <Modal.Body>{this.renderForm()}</Modal.Body>

                <Modal.Footer>
                    <Button
                        bsClass="cancel"
                        onClick={() => this.props.onClose()}
                    >
                        Cancel
                    </Button>
                    <Button
                        bsClass="primary"
                        onClick={this.onSaveSeedProduct}
                        disabled={!this.state.formValid}
                    >
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
export default connector(SeedForm);



// WEBPACK FOOTER //
// ./src/components/Products/SeedForm.tsx